import { Component, DestroyRef, effect, inject, input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { SidepanelService } from '@shared/components/sidepanel/sidepanel.service';
import { ToastrErrorComponent } from '@shared/components/toastr/toastr-error.component';
import { LabelRequiredDirective } from '@shared/directives/label-required.directive';
import { HlmDatePickerImports } from '@spartan-ng/helm/date-picker';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmLabelImports } from '@spartan-ng/helm/label';
import { HlmTextareaImports } from '@spartan-ng/helm/textarea';
import { QuillModule } from 'ngx-quill';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';
import { MessageInformationStore } from '../store/message-information.store';
import { Message } from '../model/message.model';

@Component({
  selector: 'app-form-message-information',
  imports: [
    HlmInputImports,
    HlmDatePickerImports,
    HlmTextareaImports,
    LabelRequiredDirective,
    HlmLabelImports,
    ReactiveFormsModule,
    HlmFieldImports,
    QuillModule,
  ],
  templateUrl: './form-message-information.component.html',
})
export class FormMessageInformationComponent implements OnInit {
  readonly #sidepanelService = inject(SidepanelService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #messageStore = inject(MessageInformationStore);
  readonly #fb = inject(FormBuilder);
  readonly message = input<Message>();

  readonly form = this.#fb.nonNullable.group({
    titre: ['', [Validators.required]],
    contenu: ['', [Validators.required]],
    date: [[new Date(Date.now())], [Validators.required]],
  });

  modules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ header: [1, 2, 3, false] }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ color: [] }, { background: [] }],
      ['blockquote', 'code-block'],
    ],
  };

  ngOnInit(): void {
    this.#sidepanelService.submit.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
      if (this.form.invalid) {
        this.form.markAllAsTouched();
        const toastrId = uuidv7();
        toast.custom(ToastrErrorComponent, {
          componentProps: {
            message: `Veuillez renseigner les champs marqués comme Requis.`,
            id: toastrId,
          },
          id: toastrId,
        });
        return;
      }

      const [dateDébut, dateFin] = this.date.value.map(
        (x) =>
          `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`,
      );

      if (this.message()) {
        this.#messageStore.modifier({
          id: this.message()!.id,
          data: {
            titre: this.titre.value,
            contenu: this.contenu.value,
            dateDebut: dateDébut,
            dateFin: dateFin,
          },
          next: () => {
            this.#messageStore.charger(false);
            this.#sidepanelService.close();
          },
        });
      } else {
        this.#messageStore.ajouter({
          data: {
            titre: this.titre.value,
            contenu: this.contenu.value,
            dateDebut: dateDébut,
            dateFin: dateFin,
          },
          next: () => this.#sidepanelService.close(),
        });
      }
    });
  }

  constructor() {
    effect(() => {
      const message = this.message();
      if (message) {
        this.form.patchValue({
          titre: message.titre,
          contenu: message.contenu,
          date: [new Date(message.dateDebut), new Date(message.dateFin)],
        });
      }
    });
  }

  get titre() {
    return this.form.get('titre') as AbstractControl<string>;
  }

  get contenu() {
    return this.form.get('contenu') as AbstractControl<string>;
  }

  get date() {
    return this.form.get('date') as AbstractControl<Date[]>;
  }
}
